import { html, customElement } from '@adobe/edex/ui/edex-lit';
import { uniq, without } from 'lodash-es';
import { BOOST_VALUES, EdexDomID, EdexTagName } from '@adobe/edex/ui/shared/constants';
import { BaseAuthElement } from '@adobe/edex/ui/components/base.element';
import { CurationSchema, EntityType, MarshalledResource, ResourceSettings } from '@adobe/edex/ui/shared/types';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { infoToast } from '@adobe/edex/ui/utils/notification';
import {
  MultiselectAddNew,
  MultiselectOption,
  MultiselectOptionDeleted,
  MultiselectOptionSelected,
} from '@adobe/edex/ui/components/shared/multiselect/multiselect';

import '@spectrum-web-components/tabs/sp-tabs.js';
import '@spectrum-web-components/tabs/sp-tab.js';
import '@spectrum-web-components/tabs/sp-tab-panel.js';
import * as routines from '@adobe/edex/ui/store/routines';
import { curationAPI, resourceAPI } from '@adobe/edex/ui/api';

import(
  /* webpackChunkName: "component/settings-dialog-element-styles" */
  /* webpackMode: "lazy" */
  './settings-dialog.scss'
);

enum Tab {
  seo = 'seo',
  curation = 'curation',
}

@customElement(EdexTagName.resourceSettingsDialog)
export class ResourceSettingsDialog extends BaseAuthElement {
  get useLightDom(): boolean {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      resource: { type: Object },
      seoFormData: { attribute: false },
      curationFormData: { attribute: false },
      allCollections: { attribute: false },
      activeTab: { attribute: false },
    };
  }

  activeTab = Tab.seo;

  resource: MarshalledResource;

  curationFormData: Partial<CurationSchema> = {};
  seoFormData: ResourceSettings['seo'] = {};
  allCollections: string[];

  actions = {
    fetchResource: routines.fetchResource.trigger,
    closeDialog: routines.closeDialog.trigger,
  };

  async firstUpdated() {
    await this.initForms();
  }

  async updated(changedProperties) {
    if (changedProperties.has('activeTab')) {
      await this.initForms();
    }
  }

  async initForms() {
    if (this.activeTab === Tab.seo) {
      this.seoFormData = { ...(this.resource.settings?.seo || {}) };
    }
    if (this.activeTab === Tab.curation) {
      this.curationFormData = {};
      await this.fetchCuration();
    }
  }

  async handleSeoUpdate() {
    const { resource, seoFormData } = this;
    const payload = { settings: { ...resource.settings, seo: seoFormData } };
    await resourceAPI.put(`/${resource.id}/settings`, payload);
    infoToast({ message: 'Resource settings updated' });
    this.actions.fetchResource({ id: resource.id });
    this.actions.closeDialog();
  }

  onSeoSettingsChange(e) {
    this.seoFormData[e.target.dataset.name] = e.target.value;
  }

  onSettingsDialogClose() {
    this.actions.closeDialog();
  }

  renderSeoForm() {
    if (this.activeTab !== Tab.seo) {
      return;
    }
    const seoTextFields: {
      name: keyof ResourceSettings['seo'];
      label: string;
    }[] = [
      {
        name: 'title',
        label: 'Title',
      },
      {
        name: 'metaDescription',
        label: 'Meta Description',
      },
    ];
    return html`
      <form class="resource-seo-form">
        ${seoTextFields.map(({ name, label }) => {
          return html`
            <div>
              <sp-field-label size="l">${label}</sp-field-label>
              <sp-textfield
                id="${name}"
                data-name="${name}"
                .value=${this.seoFormData[name] || ''}
                .label=${label}
                .placeholder=${label}
                multiline
                @change=${this.onSeoSettingsChange}
              ></sp-textfield>
            </div>
          `;
        })}
        <div>
        <sp-field-label size="l">${$t(I18nKey.seoSettingsIndexHeading)}</sp-field-label>
        <sp-checkbox
          id="resource-seo-noindex"
          data-id="no-index"
          data-name="noIndex"
          ?checked="${this.seoFormData.noIndex}"
          @change=${(e) => {
            this.seoFormData[e.target.dataset.name] = e.target.__checked;
          }}
        >
        ${$t(I18nKey.seoSettingsIndexLabel)}
        </sp-checkbox>
        <div>
        <sp-button-group dir="rtl">
          <sp-button variant="cta" @click="${this.handleSeoUpdate}">
            ${$t(I18nKey.submit)}
          </sp-button>
          <sp-button variant="secondary" @click="${this.onSettingsDialogClose}">
            ${$t(I18nKey.noCancel)}
          </sp-button>
        </sp-button-group>
      </form>
    `;
  }

  renderCurationForm() {
    if (this.activeTab !== Tab.curation) {
      return;
    }

    const { allCollections = [], curationFormData: { boost = 0, collections = [] } = {} } = this;

    const label2option = (label) => ({
      label,
      id: label,
    });

    const selectedCollections: MultiselectOption[] = collections.map(label2option);
    const collectionOptions = without(allCollections, ...collections).map(label2option);

    const boostOptions = BOOST_VALUES;

    return html`
      <form class="resource-curation-form">
        <sp-field-label for="boost" size="l">Boost</sp-field-label>
        <sp-radio-group id="boost" name="example" vertical>
          ${boostOptions.map(
            (value) =>
              html`
                <sp-radio
                  value="${value}"
                  ?checked="${value == boost /* use "==" for coercion */}"
                  @change="${(e) => {
                    this.curationFormData.boost = value;
                    this.requestUpdate();
                  }}"
                >
                  ${value ? value : 'None'}
                </sp-radio>
              `
          )}
        </sp-radio-group>

        <sp-field-label for="collections" size="l">Collections</sp-field-label>
        <edex-multiselect
          id="collections"
          placeholder="Search and select collections or add new ones"
          .selected=${selectedCollections}
          .options=${collectionOptions}
          taggable
          internalSearch
          @click=${this.fetchCollections}
          @selected=${this.handleCollectionSelected}
          @new=${this.handleCollectionNew}
          @deleted=${this.handleCollectionDeleted}
        ></edex-multiselect>

        <sp-button-group dir="rtl">
          <sp-button variant="cta" @click="${this.handleCurationUpdate}">${$t(I18nKey.submit)}</sp-button>
          <sp-button variant="secondary" @click="${this.onSettingsDialogClose}">${$t(I18nKey.noCancel)}</sp-button>
        </sp-button-group>
      </form>
    `;
  }

  async fetchCollections() {
    if (this.allCollections) {
      return;
    }
    const {
      data: { data },
    } = await curationAPI.get('/collections');
    this.allCollections = data;
  }

  handleCollectionNew({ detail: { label } }: CustomEvent<MultiselectAddNew>) {
    const { curationFormData: { collections = [] } = {} } = this;
    this.curationFormData.collections = [...without(collections, label), label];
    this.requestUpdate();
  }

  handleCollectionSelected({ detail: { id } }: CustomEvent<MultiselectOptionSelected>) {
    const { curationFormData: { collections = [] } = {} } = this;
    this.curationFormData.collections = [...without(collections, id), id];
    this.requestUpdate();
  }

  handleCollectionDeleted({ detail: { id } }: CustomEvent<MultiselectOptionDeleted>) {
    const { curationFormData: { collections = [] } = {} } = this;
    this.curationFormData.collections = without(collections, id);
    this.requestUpdate();
  }

  async handleCurationUpdate() {
    const {
      curationFormData: { boost, collections },
    } = this;
    const { data } = await curationAPI.put<CurationSchema>(`/${EntityType.Resource}/${this.resource.id}`, {
      boost,
      collections: uniq(collections),
    });
    this.curationFormData = data;
    infoToast({ message: 'Resource curation updated' });
    this.actions.closeDialog();
  }

  async fetchCuration() {
    const { data } = await curationAPI.get(this.resource.id, {
      validateStatus: null,
    });
    this.curationFormData = data;
  }

  render() {
    return html`
      <edex-dialog-next
        id="${EdexDomID.resourceSettingsDialog}"
        header="Resource Settings"
        open
        .divider="${false}"
        @close="${this.onSettingsDialogClose}"
      >
        <div slot="content">
          <sp-tabs selected="${this.activeTab}">
            <sp-tab label="Seo" value="${Tab.seo}" @click="${() => (this.activeTab = Tab.seo)}"></sp-tab>
            <sp-tab label="Curation" value="${Tab.curation}" @click="${() => (this.activeTab = Tab.curation)}"></sp-tab>
            <sp-tab-panel value="${Tab.seo}">${this.renderSeoForm()}</sp-tab-panel>
            <sp-tab-panel value="${Tab.curation}">${this.renderCurationForm()}</sp-tab-panel>
          </sp-tabs>
        </div>
      </edex-dialog-next>
    `;
  }
}
